import React, { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { providerMeta } from "./providerMeta";
import type { RunLeg } from "./runProgress";
import type { Question } from "@/types/vb";

// Grounded activity log. The backend exposes per-provider answered-counts, not
// individual question-completion events, so we synthesize the prototype's
// streaming feed from REAL data: each time a provider's `done` count rises, the
// newly-completed slots are emitted as rows (newest on top, older rows fade).
// Every row corresponds to a question that genuinely has an answer - we just
// can't know the exact provider-internal ordering, so slots are paired with the
// buyer's questions by index (best-effort, with a generic fallback).

interface LogRow {
  key: string; // "{PROVIDER}:{index}" - dedupes across polls
  provider: string;
  text: string;
}

const MAX_VISIBLE = 6;

export function RunActivityLog({ legs, questions }: { legs: RunLeg[]; questions: Question[] }) {
  const seen = useRef<Set<string>>(new Set());
  const [rows, setRows] = useState<LogRow[]>([]);

  // Signature of per-provider progress; changes only when a count advances, so
  // the diff effect doesn't re-run on every identical poll.
  const sig = legs.map((l) => `${l.provider}:${l.done}`).join(",");

  useEffect(() => {
    const fresh: LogRow[] = [];
    for (const leg of legs) {
      for (let i = 0; i < leg.done; i++) {
        const key = `${leg.provider}:${i}`;
        if (seen.current.has(key)) continue;
        seen.current.add(key);
        // Loose pairing: legs count answered slots against the run's question
        // snapshot; we label them from the buyer's current questions by index.
        // If questions were edited mid-run the text may not line up exactly, so
        // fall back to a generic label. Cosmetic only.
        const text = questions[i]?.text ?? `Answered question ${i + 1}`;
        fresh.push({ key, provider: leg.provider, text });
      }
    }
    if (fresh.length > 0) {
      // Prepend newest; cap what we keep so the list can't grow unbounded.
      setRows((prev) => [...fresh.reverse(), ...prev].slice(0, MAX_VISIBLE * 3));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, questions]);

  const visible = rows.slice(0, MAX_VISIBLE);

  // The grounded feed only has rows when the backend reports per-provider
  // progress (Cloud-Tasks mode). The local inline worker doesn't, so rather
  // than fake a stream we show a calm slim status; the overall bar above
  // already conveys progress.
  if (visible.length === 0) {
    return (
      <p className="vb-run-log__status" aria-live="polite">
        Querying each provider with the buyer&rsquo;s questions. This page updates on its own.
      </p>
    );
  }

  return (
    // No aria-live here: rows stream in on every poll and would flood a screen
    // reader. The progressbar above carries the announced status; this log is a
    // visual reassurance feed.
    <div className="vb-run-log" aria-label="Answers as they arrive">
      {visible.map((row, r) => {
        const meta = providerMeta(row.provider);
        return (
          <div
            key={row.key}
            className={cn("vb-run-log__row", meta.className)}
            style={{ opacity: Math.max(0.25, 1 - r * 0.16) }}
          >
            <span className="logo">{meta.logo}</span>
            <span className="q">&ldquo;{row.text}&rdquo;</span>
            <span className="ok">
              <Check size={14} aria-hidden="true" />
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default RunActivityLog;
